import { useMemo, useState } from 'react';
import { View, Text, StyleSheet, Linking } from 'react-native';
import { ExternalLink, Link } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, withWeight } from '../../theme';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { TextLink } from '../common/TextLink';
import { SegmentedControl } from '../common/SegmentedControl';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { useUIStore } from '../../store/uiStore';
import { formatRb } from '../../utils/money';
import { planBundleOpportunityUrl, shareBundleOpportunity } from '../../utils/share';
import { MAX_BID_AMOUNT } from '../../schemas/firestoreSchemas';
import type { FundingRequest, PlanBid } from '../../types';

// The owner's view of vendor bids on one bundle (Oct 2026, Glenn's CFSC
// vendor idea): open or close the bundle to bids, copy the public link, and
// accept or decline each vendor. Accept puts the vendor on the bundle team
// (defensePlansStore.acceptBid), so Ask for an update reaches them. An accepted
// bid can then ask for simulated money (Redi bucks, docs/redi-bucks.md): the
// owner names an amount and a moderator picks the fund. Hosted by a
// CompanionPanel on the desktop shell and by DefensePlanBidsSheet below it,
// like the events view.

const NO_BIDS: PlanBid[] = [];
const NO_REQUESTS: FundingRequest[] = [];

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const shortDate = (ms: number) =>
  new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(ms));

const toast = (message: string) => useUIStore.getState().showToast({ message });

/** "2 bids", "No bids yet". */
export function bidCountLabel(n: number): string {
  return n === 0 ? 'No bids yet' : `${n} ${n === 1 ? 'bid' : 'bids'}`;
}

const digitsOnly = (text: string) => text.replace(/[^0-9]/g, '');

/**
 * Where an accepted bid's money stands, and the owner's one move on it. Owners
 * never see a fund's balance: they name an amount, and a moderator picks the
 * fund. Closed requests stay on show, dimmed, because a declined one may say why.
 */
function BidFunding({
  bid,
  planId,
  bundleId,
  request,
  workDone,
}: {
  bid: PlanBid;
  planId: string;
  bundleId: string;
  request: FundingRequest | null;
  workDone: boolean;
}) {
  const { colors } = useTheme();
  const requestFunding = useDefensePlansStore((s) => s.requestFunding);
  const withdrawFundingRequest = useDefensePlansStore((s) => s.withdrawFundingRequest);
  const [asking, setAsking] = useState(false);
  const [amountText, setAmountText] = useState(bid.amount != null ? String(bid.amount) : '');
  const [busy, setBusy] = useState(false);
  const status = request?.status ?? null;
  const prefix = `plan-bid-${bid.id}`;

  const handleSend = async () => {
    const amount = parseInt(amountText, 10);
    if (!Number.isInteger(amount) || amount < 1) {
      toast('Enter the amount as a whole number');
      return;
    }
    setBusy(true);
    const error = await requestFunding(planId, bundleId, bid.id, amount);
    setBusy(false);
    if (error) {
      toast(error);
      return;
    }
    setAsking(false);
    toast('Funding requested');
  };
  const handleWithdraw = async () => {
    if (!request) return;
    setBusy(true);
    const error = await withdrawFundingRequest(request.id);
    setBusy(false);
    if (error) toast(error);
  };

  if (status === 'requested' && request) {
    return (
      <View
        style={styles.fundingLine}
        testID={`${prefix}-funding`} // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
        dataSet={{ status }}>
        <Text style={[styles.meta, styles.fundingText, { color: colors.textSecondary }]}>
          {`Funding requested ${shortDate(request.requestedAt.toMillis())} · ${formatRb(request.amount)}`}
        </Text>
        <Button
          title="Withdraw"
          onPress={() => void handleWithdraw()}
          variant="ghost"
          size="xs"
          disabled={busy}
          testID={`${prefix}-withdraw-funding`}
        />
      </View>
    );
  }
  if (status === 'committed' && request) {
    return (
      <View
        testID={`${prefix}-funding`} // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
        dataSet={{ status }}>
        <Text style={[styles.meta, { color: colors.textSecondary }]}>
          {`Funded from ${request.fundName ?? 'a fund'} · ${formatRb(request.amount)}`}
        </Text>
        {!workDone && (
          <Text style={[styles.meta, { color: colors.textTertiary }]}>
            Mark the work done to release payment.
          </Text>
        )}
      </View>
    );
  }
  if (status === 'paid' && request) {
    return (
      <Text
        style={[styles.meta, { color: colors.textSecondary }]}
        testID={`${prefix}-funding`}
        // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
        dataSet={{ status }}>
        {`Paid ${shortDate((request.paidAt ?? request.updatedAt).toMillis())} · ${formatRb(request.amount)}`}
      </Text>
    );
  }

  const closedText =
    status === 'declined'
      ? 'Funding declined'
      : status === 'withdrawn'
        ? 'Funding request withdrawn'
        : status === 'cancelled'
          ? 'Funding cancelled'
          : null;
  return (
    <View style={styles.fundingAsk}>
      {closedText && (
        <Text
          style={[styles.meta, styles.dimmed, { color: colors.textSecondary }]}
          testID={`${prefix}-funding`}
          // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
          dataSet={{ status }}>
          {request?.decisionNote ? `${closedText}: ${request.decisionNote}` : closedText}
        </Text>
      )}
      {asking ? (
        <>
          <Text style={[styles.hint, { color: colors.textSecondary }]}>
            A program moderator reviews requests and picks the fund.
          </Text>
          <Input
            compact
            label="Amount in RB"
            value={amountText}
            onChangeText={(text) => setAmountText(digitsOnly(text))}
            keyboardType="number-pad"
            placeholder="12000"
            testID={`${prefix}-funding-amount`}
          />
          <View style={styles.rowActions}>
            <Button
              title="Send request"
              onPress={() => void handleSend()}
              size="xs"
              disabled={busy || amountText === ''}
              testID={`${prefix}-send-funding`}
            />
            <Button
              title="Cancel"
              onPress={() => setAsking(false)}
              variant="secondary"
              size="xs"
              disabled={busy}
              testID={`${prefix}-cancel-funding`}
            />
          </View>
        </>
      ) : (
        <View style={styles.rowActions}>
          <Button
            title="Request funding"
            onPress={() => setAsking(true)}
            variant="secondary"
            size="xs"
            testID={`${prefix}-request-funding`}
          />
        </View>
      )}
    </View>
  );
}

function EstimateField({
  planId,
  bundleId,
  estimate,
}: {
  planId: string;
  bundleId: string;
  estimate: number | undefined;
}) {
  const setBundleEstimate = useDefensePlansStore((s) => s.setBundleEstimate);
  const [text, setText] = useState(estimate != null ? String(estimate) : '');
  const handleBlur = async () => {
    const next = text === '' ? null : Math.min(parseInt(text, 10), MAX_BID_AMOUNT);
    setText(next == null ? '' : String(next));
    const ok = await setBundleEstimate(planId, bundleId, next);
    if (!ok) toast("Couldn't save the estimate");
  };
  return (
    <Input
      compact
      label="Estimate"
      hint="Vendors don't see this."
      value={text}
      onChangeText={(next) => setText(digitsOnly(next))}
      onBlur={() => void handleBlur()}
      keyboardType="number-pad"
      placeholder="12000"
      testID={`plan-bundle-${bundleId}-estimate`}
    />
  );
}

function BidRow({
  bid,
  planId,
  onTeam,
  busy,
  request,
  workDone,
  onAccept,
  onDecline,
}: {
  bid: PlanBid;
  planId: string;
  onTeam: boolean;
  busy: boolean;
  request: FundingRequest | null;
  workDone: boolean;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const { colors } = useTheme();
  const declined = bid.status === 'declined';
  const contact = [bid.email, bid.phone].filter(Boolean).join(' · ');
  return (
    <View
      testID={`plan-bid-${bid.id}`}
      data-status={bid.status}
      style={[styles.row, { borderColor: colors.border }, declined && styles.dimmed]}>
      <View style={styles.rowTop}>
        <Text style={[styles.rowTitle, { color: colors.text }]} numberOfLines={2}>
          {`${bid.organization}, ${bid.person}`}
        </Text>
        <Text style={[styles.amount, { color: colors.text }]}>
          {bid.amount != null ? usd.format(bid.amount) : 'No price given'}
        </Text>
      </View>
      {bid.note ? (
        <Text style={[styles.note, { color: colors.textSecondary }]}>{bid.note}</Text>
      ) : null}
      <Text style={[styles.meta, { color: colors.textSecondary }]} selectable>
        {contact}
      </Text>
      <Text style={[styles.meta, { color: colors.textTertiary }]}>
        {`Sent ${shortDate(bid.updatedAt.toMillis())}`}
      </Text>
      {bid.status === 'submitted' ? (
        <View style={styles.rowActions}>
          <Button
            title="Accept"
            onPress={onAccept}
            size="xs"
            disabled={busy}
            testID={`plan-bid-${bid.id}-accept`}
          />
          <Button
            title="Decline"
            onPress={onDecline}
            variant="secondary"
            size="xs"
            disabled={busy}
            testID={`plan-bid-${bid.id}-decline`}
          />
        </View>
      ) : (
        <Text
          style={[styles.statusText, { color: declined ? colors.textSecondary : colors.success }]}
          testID={`plan-bid-${bid.id}-status`}>
          {declined ? 'Declined' : onTeam ? 'On the team' : 'Accepted'}
        </Text>
      )}
      {bid.status === 'accepted' && (
        <BidFunding
          bid={bid}
          planId={planId}
          bundleId={bid.bundleId}
          request={request}
          workDone={workDone}
        />
      )}
    </View>
  );
}

export function DefensePlanBidsView({
  planId,
  bundleId,
  hideTitle = false,
  onFindVendors,
}: {
  planId: string;
  bundleId: string;
  hideTitle?: boolean;
  /** Opens the vendor directory for this bundle (Nov 2026). */
  onFindVendors?: () => void;
}) {
  const { colors } = useTheme();
  const plan = useDefensePlansStore((s) => (s.ownPlan?.id === planId ? s.ownPlan : null));
  const allBids = useDefensePlansStore((s) => s.bidsByPlan[planId] ?? NO_BIDS);
  const parties = useDefensePlansStore((s) => s.partiesByPlan[planId]);
  const fundingRequests = useDefensePlansStore(
    (s) => s.fundingRequestsByPlan[planId] ?? NO_REQUESTS
  );
  const setBundleBidsOpen = useDefensePlansStore((s) => s.setBundleBidsOpen);
  const acceptBid = useDefensePlansStore((s) => s.acceptBid);
  const declineBid = useDefensePlansStore((s) => s.declineBid);
  const setVisibility = useDefensePlansStore((s) => s.setVisibility);
  const [busyId, setBusyId] = useState<string | null>(null);

  const bundle = plan?.bundles.find((b) => b.id === bundleId) ?? null;
  const bids = useMemo(() => allBids.filter((b) => b.bundleId === bundleId), [allBids, bundleId]);
  if (!plan || !bundle) {
    return (
      <View style={styles.content}>
        <Text style={[styles.hint, { color: colors.textSecondary }]}>
          This bundle is no longer on the plan.
        </Text>
      </View>
    );
  }
  const open = !!bundle.bidsOpen;
  const shared = plan.visibility === 'shared';

  const handleToggle = async (next: 'open' | 'closed') => {
    if ((next === 'open') === open) return;
    const ok = await setBundleBidsOpen(planId, bundleId, next === 'open');
    if (!ok) toast("Couldn't change the bids setting");
  };
  const handleCopy = async () => {
    const result = await shareBundleOpportunity({ planId, bundleId, bundleName: bundle.name });
    if (result === 'copied') toast('Link copied');
    if (result === 'failed') toast("Couldn't copy the link");
  };
  const handleAnswer = async (bid: PlanBid, answer: 'accept' | 'decline') => {
    setBusyId(bid.id);
    const ok =
      answer === 'accept' ? await acceptBid(planId, bid.id) : await declineBid(planId, bid.id);
    setBusyId(null);
    if (answer === 'accept') {
      toast(ok ? `${bid.organization} is on the team` : "Couldn't accept the bid");
    } else if (!ok) {
      toast("Couldn't decline the bid");
    }
  };
  const handleShareThePlan = async () => {
    const saved = await setVisibility('shared');
    toast(saved ? 'Plan shared' : "Couldn't change who can see this plan");
  };

  return (
    <View style={styles.content} testID={`plan-bundle-${bundleId}-bids`}>
      {!hideTitle && <Text style={[styles.title, { color: colors.text }]}>{bundle.name}</Text>}

      <View style={styles.field}>
        <Text style={[styles.label, { color: colors.textSecondary }]}>Vendor bids</Text>
        <SegmentedControl
          options={[
            { key: 'open', label: 'Open' },
            { key: 'closed', label: 'Closed' },
          ]}
          value={open ? 'open' : 'closed'}
          onChange={(key) => void handleToggle(key)}
          ariaLabel="Vendor bids on this bundle"
          role="radiogroup"
          testID={`plan-bundle-${bundleId}-bids-toggle`}
        />
        <Text style={[styles.hint, { color: colors.textSecondary }]}>
          {open
            ? 'Vendors with the link can say they can help and give a price. Once you accept one, you can ask your program to fund it.'
            : 'Open the bundle to let vendors with the link say they can help.'}
        </Text>
      </View>

      <EstimateField
        key={bundleId}
        planId={planId}
        bundleId={bundleId}
        estimate={bundle.estimate}
      />

      {open && !shared && (
        <View testID="plan-bids-private-note" style={styles.privateNote}>
          <Text style={[styles.hint, { color: colors.textSecondary }]}>
            This plan is private, so the link won&apos;t open for vendors until you share it.
          </Text>
          <TextLink
            label="Share the plan"
            onPress={() => void handleShareThePlan()}
            textStyle={TYPOGRAPHY.caption}
            testID="plan-bids-share-plan"
          />
        </View>
      )}

      {onFindVendors && (
        <View style={styles.privateNote}>
          <Text style={[styles.hint, { color: colors.textSecondary }]}>
            {bundle.invitedVendors?.length
              ? `${bundle.invitedVendors.length} ${bundle.invitedVendors.length === 1 ? 'vendor' : 'vendors'} invited from the vendor list.`
              : 'Pick local vendors from the shared list and email them the link.'}
          </Text>
          <TextLink
            label="Find vendors"
            onPress={onFindVendors}
            textStyle={TYPOGRAPHY.caption}
            testID="plan-bids-find-vendors"
          />
        </View>
      )}

      {open && (
        <View style={styles.actionGrid}>
          <Button
            title="Copy link"
            onPress={() => void handleCopy()}
            variant="secondary"
            size="sm"
            leftIcon={Link}
            style={styles.half}
            testID="plan-bids-copy"
          />
          <Button
            title="Open page"
            onPress={() => void Linking.openURL(planBundleOpportunityUrl(planId, bundleId))}
            variant="secondary"
            size="sm"
            rightIcon={ExternalLink}
            style={styles.half}
            testID="plan-bids-open"
          />
        </View>
      )}

      <Text style={[styles.overline, { color: colors.textSecondary }]} accessibilityRole="header">
        {bids.length === 0 ? 'Bids' : `Bids (${bids.length})`}
      </Text>
      {bids.length === 0 ? (
        <Text style={[styles.hint, { color: colors.textTertiary }]} testID="plan-bids-empty">
          No bids yet. Share the opportunity link with vendors.
        </Text>
      ) : (
        <View style={styles.list}>
          {bids.map((bid) => (
            <BidRow
              key={bid.id}
              bid={bid}
              planId={planId}
              request={fundingRequests.find((r) => r.bidId === bid.id) ?? null}
              workDone={!!bundle.workDoneAt}
              onTeam={!!bid.acceptedPartyId && !!parties?.[bid.acceptedPartyId]}
              busy={busyId != null}
              onAccept={() => void handleAnswer(bid, 'accept')}
              onDecline={() => void handleAnswer(bid, 'decline')}
            />
          ))}
        </View>
      )}
      <View testID="plan-bids-end" style={styles.scrollSentinel} />
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
    gap: SPACING.sm,
  },
  title: {
    ...TYPOGRAPHY.h4,
  },
  field: {
    gap: SPACING.xs,
  },
  label: {
    ...TYPOGRAPHY.caption,
  },
  hint: {
    ...TYPOGRAPHY.caption,
  },
  privateNote: {
    gap: SPACING.xs,
    alignItems: 'flex-start',
  },
  actionGrid: {
    flexDirection: 'row',
    gap: SPACING.sm,
  },
  half: {
    flex: 1,
  },
  // With the content gap (sm), 24 above the section label and 8 below it.
  overline: {
    ...TYPOGRAPHY.overline,
    marginTop: SPACING.md,
  },
  list: {
    gap: SPACING.xs,
  },
  row: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.sm,
    gap: 2,
  },
  dimmed: {
    opacity: 0.6,
  },
  rowTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: SPACING.sm,
  },
  rowTitle: {
    ...withWeight(TYPOGRAPHY.bodySmall, 'medium'),
    flex: 1,
    minWidth: 0,
  },
  amount: {
    ...withWeight(TYPOGRAPHY.bodySmall, 'medium'),
  },
  note: {
    ...TYPOGRAPHY.bodySmall,
  },
  meta: {
    ...TYPOGRAPHY.caption,
  },
  rowActions: {
    flexDirection: 'row',
    gap: SPACING.xs,
    marginTop: SPACING.xs,
  },
  fundingLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    marginTop: SPACING.xxs,
  },
  fundingText: {
    flex: 1,
    minWidth: 0,
  },
  fundingAsk: {
    gap: SPACING.xs,
    marginTop: SPACING.xxs,
  },
  statusText: {
    ...TYPOGRAPHY.caption,
    marginTop: SPACING.xxs,
  },
  scrollSentinel: {
    height: 1,
  },
});
